import './shared/string.extensions'
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';

//import * as Sentry from "@sentry/angular";

import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { CarouselModule } from 'primeng/carousel';
import { ProgressBarModule } from 'primeng/progressbar';
import { ChartModule } from 'primeng/chart';
import { AvatarModule } from 'primeng/avatar';
import { AccordionModule } from 'primeng/accordion';

// Application Components
import { AppComponent, createTranslateLoader } from './app.component';
import { AppBreadcrumbComponent } from 'src/app/layout/breadcrumb/app.breadcrumb.component';
import { AppMainComponent } from './app.main.component';
import { AppRightMenuComponent } from './layout/rightmenu/app.rightmenu.component';
import { AppInlineMenuComponent } from './layout/inlinemenu/app.inlinemenu.component';
import { AppMenuComponent } from './layout/menu/app.menu.component';
import { AppMenuitemComponent } from './layout/menu/app.menuitem.component';
import { AppTopbarComponent } from './layout/topbar/app.topbar.component';
import { AppFooterComponent } from './layout/footer/app.footer.component';

import { AppHelpComponent } from './pages/app.help.component';
import { AppNotfoundComponent } from './pages/app.notfound.component';
import { AppErrorComponent } from './pages/app.error.component';
import { AppAccessdeniedComponent } from './pages/app.accessdenied.component';

// Application services
import { MenuService } from './layout/menu/app.menu.service';
import { AppBreadcrumbService } from './layout/breadcrumb/app.breadcrumb.service';

import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { LucideAngularModule, LayoutDashboard, CalendarDays, Network, Tags, PersonStanding, UsersRound, HandCoins, List, Settings, Newspaper, Send, FileText, CreditCard, UserRound, Menu, X, ChevronDown, LogOut } from 'lucide-angular';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import localeEs from '@angular/common/locales/es';
import localeExtraEs from '@angular/common/locales/extra/es';

import { registerLocaleData } from '@angular/common';
import { NgxPermissionsModule } from 'ngx-permissions';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule as InputSwitchModule } from 'primeng/toggleswitch';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastNotifierService } from './shared/toast-notifier.service';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';

import { FIREBASE_OPTIONS } from '@angular/fire/compat';

import { USE_EMULATOR as USE_AUTH_EMULATOR } from '@angular/fire/compat/auth';
import { USE_EMULATOR as USE_FIRESTORE_EMULATOR } from '@angular/fire/compat/firestore';

import { environment } from '../environments/environment';
import { AuthService } from './auth/auth.service';
import { ConfirmDialogService } from './shared/confirm-dialog.service';
import { TokenInterceptor } from './auth/auth.interceptor';
import { SignalrService } from './shared/signalr.service';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeuix/themes/lara';
registerLocaleData(localeEs, 'es-ES', localeExtraEs);

@NgModule({ declarations: [
        AppComponent,
        AppBreadcrumbComponent,
        AppMainComponent,
        AppMenuComponent,
        AppMenuitemComponent,
        AppRightMenuComponent,
        AppInlineMenuComponent,
        AppTopbarComponent,
        AppFooterComponent,
        AppHelpComponent,
        AppNotfoundComponent,
        AppErrorComponent,
        AppAccessdeniedComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        FormsModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        BreadcrumbModule,
        ButtonModule,
        CardModule,
        ConfirmDialogModule,
        TableModule,
        CarouselModule,
        ProgressBarModule,
        ChartModule,
        SelectModule,
        AvatarModule,
        AccordionModule,
        DatePickerModule,
        InputTextModule,
        InputNumberModule,
        InputSwitchModule,
        ToastModule,
        FontAwesomeModule,
        LucideAngularModule.pick({ LayoutDashboard, CalendarDays, Network, Tags, PersonStanding, UsersRound, HandCoins, List, Settings, Newspaper, Send, FileText, CreditCard, UserRound, Menu, X, ChevronDown, LogOut }),
        AvatarModule,
        DialogModule,
        EditorModule,
        NgxPermissionsModule.forRoot(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [HttpClient]
            }
        })], providers: [
        AuthService,
        MenuService,
        AppBreadcrumbService,
        MessageService,
        ConfirmDialogService,
        ConfirmationService,
        ToastNotifierService,
        //{
        //  provide: ErrorHandler,
        //  useValue: Sentry.createErrorHandler({
        //    showDialog: false,
        //  }),
        //},
        //{
        //  provide: Sentry.TraceService,
        //  deps: [Router],
        //},
        //{
        //  provide: APP_INITIALIZER,
        //  useFactory: () => () => { },
        //  deps: [Sentry.TraceService],
        //  multi: true,
        //},
        {
            provide: LOCALE_ID,
            useValue: "es-ES"
        },
        { provide: FIREBASE_OPTIONS, useValue: environment.firebase },
        { provide: USE_AUTH_EMULATOR, useValue: environment.useEmulators ? ['http://localhost', 9099] : undefined },
        { provide: USE_FIRESTORE_EMULATOR, useValue: environment.useEmulators ? ['localhost', 8081] : undefined },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
        SignalrService,
        providePrimeNG({
            theme: {
                preset: Lara,
                options: { darkModeSelector: '.dark' }
            }
        }),
        provideHttpClient(withInterceptorsFromDi())
    ] })

export class AppModule {
  constructor(library: FaIconLibrary) {
    // Add an icon to the library for convenient access in other components
    library.addIconPacks(fas, far);
  }
}

